import { useEffect, useRef } from 'react';

// Keeps the IsraelGives hosted-payment iframe as tall as its content.
//
// Their form already loads givingtech-form-resize.js, which posts
// { type: 'givingtech-resize', height } to us whenever its document height
// changes. We were never listening, so the frame sat at the CSS min-height
// (.mf-iframe, 480px) and anything taller was clipped with no scrollbar of
// ours to reach it — the foreign-currency address fields, their validation
// messages, and the extra step bit adds to the flow.
//
// Scope note: this follows DOCUMENT height. A payment method that renders a
// position:fixed overlay inside their page (Bank Hapoalim's bit SDK does)
// doesn't grow scrollHeight, so it's still bounded by however tall the frame
// happens to be — this gives it more room, it doesn't guarantee enough.

// Their beacon posts to '*' (it's only sending a height out), so validating
// the sender is on us. Must match the origin igIframe.js POSTs to.
const IG_ORIGIN = 'https://secured.israelgives.org';

// Floor matches .mf-iframe's min-height: their page reports small heights
// mid-load, and honouring one would collapse the frame under the donor.
const MIN_HEIGHT = 480;
// Nothing they render is legitimately this tall; the cap just stops a bogus
// number from producing a page-breaking frame.
const MAX_HEIGHT = 2400;

// Returns a ref to put on the <iframe>. One frame per page — the message is
// matched to this hook's own frame by contentWindow, so a page that ever
// mounts two won't have one resizing the other.
export function useIgFrameHeight() {
  const frameRef = useRef(null);

  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== IG_ORIGIN) return;
      const frame = frameRef.current;
      if (!frame || e.source !== frame.contentWindow) return;
      if (!e.data || e.data.type !== 'givingtech-resize') return;

      const height = Number(e.data.height);
      if (!Number.isFinite(height) || height <= 0) return;

      frame.style.height = `${Math.min(Math.max(height, MIN_HEIGHT), MAX_HEIGHT)}px`;
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return frameRef;
}
